import React, { useEffect, useRef, useState } from "react";

import { useClientParamsHooks, useModals } from "hooks";
import { IFolder } from "store/types";

import { useClientWorkspaceCtx } from "../context/ClientWorkspace-context";

const DRAG_OVER_DELAY_MS = 150;

interface UseFolderDragDropOptions {
  folder: IFolder;
  onDropFiles?: (files: File[]) => void;
}

export function useFolderDragDrop({ onDropFiles }: UseFolderDragDropOptions) {
  const [isDragOver, setIsDragOver] = useState(false);
  const dragOverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (dragOverTimerRef.current !== null) clearTimeout(dragOverTimerRef.current);
    };
  }, []);

  const clearDragOverTimer = () => {
    if (dragOverTimerRef.current !== null) {
      clearTimeout(dragOverTimerRef.current);
      dragOverTimerRef.current = null;
    }
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const { types } = e.dataTransfer;
    // Only native file drags are a real drop target (they upload into this
    // folder via `onDropFiles`). There's no client move endpoint, so metadata /
    // folder drags are NOT highlighted — otherwise the tile advertises a drop
    // that does nothing.
    const shouldHighlight = types.includes("Files") && !!onDropFiles;

    if (shouldHighlight) {
      if (dragOverTimerRef.current === null && !isDragOver) {
        dragOverTimerRef.current = setTimeout(() => {
          dragOverTimerRef.current = null;
          setIsDragOver(true);
        }, DRAG_OVER_DELAY_MS);
      }
    } else {
      clearDragOverTimer();
    }
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      clearDragOverTimer();
      setIsDragOver(false);
    }
  };

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    clearDragOverTimer();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length && onDropFiles) {
      onDropFiles(files);
    }
  };

  return { isDragOver, onDragOver, onDragLeave, onDrop };
}

export function useWorkspaceFileDrop() {
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  const { refetchFolderMetadata } = useClientWorkspaceCtx();
  const { clientId } = useClientParamsHooks();
  const { filesUploadModal } = useModals();

  const isNativeFileDrag = (e: React.DragEvent) => e.dataTransfer.types.includes("Files");

  useEffect(() => {
    const reset = () => {
      dragCounterRef.current = 0;
      setIsDragOver(false);
    };
    document.addEventListener("drop", reset, true);
    const onDocDragLeave = (e: DragEvent) => {
      if (!e.relatedTarget) reset();
    };
    document.addEventListener("dragleave", onDocDragLeave);
    return () => {
      document.removeEventListener("drop", reset, true);
      document.removeEventListener("dragleave", onDocDragLeave);
    };
  }, []);

  const handleDragEnter = (e: React.DragEvent) => {
    if (!isNativeFileDrag(e)) return;
    e.preventDefault();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) setIsDragOver(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!isNativeFileDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!isNativeFileDrag(e)) return;
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (!files.length) return;
    filesUploadModal({
      clientId,
      files,
      onSuccessCallback: () => refetchFolderMetadata()
    });
  };

  return { isDragOver, handleDragEnter, handleDragOver, handleDragLeave, handleDrop };
}
